import React, { useState, useEffect } from 'react';
import {
    ArrowLeftRight,
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    Upload,
    AlertCircle,
    DollarSign,
    User,
    Eye,
    Award
} from 'lucide-react';
import { p2pService } from '../services/p2pService';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../contexts/AuthContext';

// Базовый URL для изображений
const STORAGE_BASE_URL = 'https://api.lite-com.ru/storage';

// Функция для получения полного URL изображения
const getImageUrl = (path) => {
    if (!path) return '';
    // Если путь уже содержит полный URL, возвращаем как есть
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    // Убираем начальный слэш если есть
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${STORAGE_BASE_URL}/${cleanPath}`;
};

const P2PExchange = () => {
    const { user } = useAuth(); // Получаем пользователя из контекста
    const [activeTab, setActiveTab] = useState('offers');
    const [offers, setOffers] = useState([]);
    const [myOffers, setMyOffers] = useState([]);
    const [myTransactions, setMyTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [selectedType, setSelectedType] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, data: null });
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 });

    // ID текущего пользователя из AuthContext
    const currentUserId = user?.id || null;

    useEffect(() => {
        loadData();
    }, [activeTab, selectedCurrency, selectedType]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'offers') {
                await loadOffers();
            } else if (activeTab === 'myOffers') {
                await loadMyOffers();
            } else if (activeTab === 'myTransactions') {
                await loadMyTransactions();
            }
        } catch (error) {
            console.error('Error loading data:', error);
            if (activeTab === 'offers') {
                setOffers([]);
            } else if (activeTab === 'myOffers') {
                setMyOffers([]);
            } else if (activeTab === 'myTransactions') {
                setMyTransactions([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const loadOffers = async () => {
        try {
            const params = { currency: selectedCurrency };
            if (selectedType !== 'all') {
                params.type = selectedType;
            }

            const response = await p2pService.getOffers(params);

            if (response.success && response.data && Array.isArray(response.data.data)) {
                setOffers(response.data.data);
                setPagination({
                    currentPage: response.data.current_page || 1,
                    totalPages: response.data.last_page || 1,
                    total: response.data.total || 0
                });
            } else {
                setOffers([]);
                setPagination({ currentPage: 1, totalPages: 1, total: 0 });
            }
        } catch (error) {
            console.error('Error loading offers:', error);
            setOffers([]);
        }
    };

    const loadMyOffers = async () => {
        try {
            const response = await p2pService.getMyOffers();
            if (response.success && response.data && Array.isArray(response.data.data)) {
                setMyOffers(response.data.data);
            } else {
                setMyOffers([]);
            }
        } catch (error) {
            console.error('Error loading my offers:', error);
            setMyOffers([]);
        }
    };

    const loadMyTransactions = async () => {
        try {
            const response = await p2pService.getMyTransactions();
            if (response.success && response.data && Array.isArray(response.data.data)) {
                setMyTransactions(response.data.data);
            } else {
                setMyTransactions([]);
            }
        } catch (error) {
            console.error('Error loading my transactions:', error);
            setMyTransactions([]);
        }
    };

    const handleCreateOffer = async (formData) => {
        try {
            const response = await p2pService.createOffer(formData);
            if (response.success) {
                alert('Объявление успешно создано!');
                setShowCreateModal(false);
                loadData();
            } else {
                alert(response.message || 'Ошибка при создании объявления');
            }
        } catch (error) {
            console.error('Error creating offer:', error);
            alert('Ошибка при создании объявления');
        }
    };

    const handleCancelOffer = async (offerId) => {
        setConfirmModal({
            isOpen: true,
            type: 'cancelOffer',
            data: offerId
        });
    };

    const executeCancelOffer = async () => {
        try {
            const response = await p2pService.cancelOffer(confirmModal.data);
            if (response.success) {
                alert('Объявление отменено');
                setConfirmModal({ isOpen: false, type: null, data: null });
                loadData();
            } else {
                alert(response.message || 'Ошибка при отмене объявления');
            }
        } catch (error) {
            console.error('Error cancelling offer:', error);
            alert('Ошибка при отмене объявления');
        }
    };

    const handleStartTransaction = (offer) => {
        setSelectedOffer(offer);
        setShowTransactionModal(true);
    };

    const handleCreateTransaction = async (formData) => {
        try {
            const response = await p2pService.createTransaction(formData);
            if (response.success) {
                alert('Сделка создана! У вас есть 60 минут для оплаты.');
                setShowTransactionModal(false);
                setSelectedOffer(null);
                setActiveTab('myTransactions');
                loadData();
            } else {
                alert(response.message || 'Ошибка при создании сделки');
            }
        } catch (error) {
            console.error('Error creating transaction:', error);
            alert('Ошибка при создании сделки');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#d5ebe6] to-white p-3 sm:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
                        <div className="flex items-center gap-3">
                            <ArrowLeftRight className="w-6 h-6 sm:w-8 sm:h-8 text-[#2b6c8e]" />
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">P2P Обмен</h1>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#2b6c8e] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-[#234d6a] transition-colors text-sm sm:text-base"
                        >
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="hidden sm:inline">Создать объявление</span>
                            <span className="sm:hidden">Создать</span>
                        </button>
                    </div>

                    <div className="flex gap-2 sm:gap-4 border-b overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
                        <button
                            onClick={() => setActiveTab('offers')}
                            className={`pb-3 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                                activeTab === 'offers'
                                    ? 'text-[#2b6c8e] border-b-2 border-[#2b6c8e]'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Все объявления
                        </button>
                        <button
                            onClick={() => setActiveTab('myOffers')}
                            className={`pb-3 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                                activeTab === 'myOffers'
                                    ? 'text-[#2b6c8e] border-b-2 border-[#2b6c8e]'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Мои объявления
                        </button>
                        <button
                            onClick={() => setActiveTab('myTransactions')}
                            className={`pb-3 px-3 sm:px-4 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
                                activeTab === 'myTransactions'
                                    ? 'text-[#2b6c8e] border-b-2 border-[#2b6c8e]'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Мои сделки
                        </button>
                    </div>
                </div>

                {activeTab === 'offers' && (
                    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Валюта</label>
                                <select
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b6c8e] focus:border-transparent"
                                >
                                    <option value="USD">USDT</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Тип</label>
                                <select
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b6c8e] focus:border-transparent"
                                >
                                    <option value="all">Все</option>
                                    <option value="buy">Покупка</option>
                                    <option value="sell">Продажа</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b6c8e]"></div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'offers' && (
                            <OffersList offers={offers} onStartTransaction={handleStartTransaction} />
                        )}
                        {activeTab === 'myOffers' && (
                            <MyOffersList offers={myOffers} onCancel={handleCancelOffer} />
                        )}
                        {activeTab === 'myTransactions' && (
                            <TransactionsList transactions={myTransactions} onRefresh={loadData} currentUserId={currentUserId} />
                        )}
                    </>
                )}

                {showCreateModal && (
                    <CreateOfferModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateOffer} />
                )}

                {showTransactionModal && selectedOffer && (
                    <CreateTransactionModal
                        offer={selectedOffer}
                        onClose={() => {
                            setShowTransactionModal(false);
                            setSelectedOffer(null);
                        }}
                        onCreate={handleCreateTransaction}
                    />
                )}

                {confirmModal.isOpen && (
                    <ConfirmModal
                        isOpen={confirmModal.isOpen}
                        onClose={() => setConfirmModal({ isOpen: false, type: null, data: null })}
                        onConfirm={() => {
                            if (confirmModal.type === 'cancelOffer') {
                                executeCancelOffer();
                            }
                        }}
                        title="Отменить объявление"
                        message="Вы уверены, что хотите отменить объявление?"
                        confirmText="Отменить"
                        cancelText="Назад"
                        type="danger"
                    />
                )}
            </div>
        </div>
    );
};

const OffersList = ({ offers, onStartTransaction }) => {
    if (!offers || offers.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
                <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-base sm:text-lg">Нет доступных объявлений</p>
            </div>
        );
    }

    return (
        <div className="grid gap-3 sm:gap-4">
            {offers.map((offer) => (
                <div key={offer.id} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div className="flex-1 w-full">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                                    offer.type === 'sell' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {offer.type === 'sell' ? 'Продажа' : 'Покупка'}
                                </span>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                                {Number(offer.available_amount).toLocaleString()} {offer.currency}
                            </p>

                            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600 mb-2">
                                <User className="w-4 h-4" />
                                <span>{offer.user?.login || 'Пользователь'}</span>
                                {offer.user?.successful_transactions > 0 && (
                                    <div className="flex items-center gap-1 ml-2 text-green-600">
                                        <Award className="w-4 h-4" />
                                        <span className="font-semibold">{offer.user.successful_transactions} сделок</span>
                                    </div>
                                )}
                            </div>

                            <p className="text-xs sm:text-sm text-gray-500 mb-1">
                                Доступно: {Number(offer.available_amount).toLocaleString()} {offer.currency}
                            </p>

                            {offer.min_amount && (
                                <p className="text-xs sm:text-sm text-gray-500 mb-2">
                                    Мин: {Number(offer.min_amount).toLocaleString()} {offer.currency}
                                </p>
                            )}

                            {/* Реквизиты оплаты */}
                            {offer.payment_details && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs font-semibold text-blue-800 mb-1">💳 Реквизиты для оплаты:</p>
                                    <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap break-all">{offer.payment_details}</p>
                                </div>
                            )}

                            {/* Условия сделки */}
                            {offer.terms && (
                                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                    <p className="text-xs font-semibold text-gray-700 mb-1">📋 Условия:</p>
                                    <p className="text-xs sm:text-sm text-gray-600">{offer.terms}</p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => onStartTransaction(offer)}
                            disabled={offer.available_amount <= 0}
                            className="w-full sm:w-auto bg-[#2b6c8e] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#234d6a] transition-colors disabled:bg-gray-300 text-sm sm:text-base"
                        >
                            {offer.type === 'sell' ? 'Купить' : 'Продать'}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

const MyOffersList = ({ offers, onCancel }) => {
    if (!offers || offers.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
                <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-base sm:text-lg">У вас нет активных объявлений</p>
            </div>
        );
    }

    return (
        <div className="grid gap-3 sm:gap-4">
            {offers.map((offer) => (
                <div key={offer.id} className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div className="flex-1 w-full">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                                    offer.type === 'sell' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {offer.type === 'sell' ? 'Продажа' : 'Покупка'}
                                </span>
                                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${
                                    offer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {offer.status === 'active' ? 'Активно' : 'Неактивно'}
                                </span>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{Number(offer.amount).toLocaleString()} {offer.currency}</p>
                            <p className="text-xs sm:text-sm text-gray-500">Доступно: {Number(offer.available_amount).toLocaleString()} {offer.currency}</p>
                            <p className="text-xs sm:text-sm text-gray-500">Активных сделок: {offer.active_transactions_count || 0}</p>
                        </div>
                        {offer.status === 'active' && offer.active_transactions_count === 0 && (
                            <button
                                onClick={() => onCancel(offer.id)}
                                className="w-full sm:w-auto bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 text-sm sm:text-base"
                            >
                                Отменить
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

const TransactionsList = ({ transactions, onRefresh, currentUserId }) => {
    if (!transactions || transactions.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 text-center">
                <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-base sm:text-lg">У вас нет сделок</p>
            </div>
        );
    }

    return (
        <div className="grid gap-3 sm:gap-4">
            {transactions.map((tx) => (
                <TransactionCard key={tx.id} transaction={tx} onRefresh={onRefresh} currentUserId={currentUserId} />
            ))}
        </div>
    );
};

const TransactionCard = ({ transaction, onRefresh, currentUserId }) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showConfirmComplete, setShowConfirmComplete] = useState(false);
    const [showProofModal, setShowProofModal] = useState(false);

    // Определяем роли с поддержкой разных форматов данных
    const buyerId = transaction.buyer_id || transaction.buyer?.id;
    const sellerId = transaction.seller_id || transaction.seller?.id;

    const isBuyer = currentUserId === buyerId;
    const isSeller = currentUserId === sellerId;

    // ВРЕМЕННАЯ ОТЛАДКА
    if (transaction.status === 'pending') {
        console.log('🔍 Debug кнопки загрузки чека:', {
            transactionId: transaction.transaction_id,
            currentUserId,
            buyerId,
            sellerId,
            isBuyer,
            isSeller,
            status: transaction.status,
            canUploadProof: isBuyer && transaction.status === 'pending'
        });
    }

    const getStatusInfo = (status) => {
        const map = {
            pending: { text: 'Ожидание оплаты', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            payment_sent: { text: 'Оплата отправлена', color: 'bg-blue-100 text-blue-800', icon: Upload },
            completed: { text: 'Завершена', color: 'bg-green-100 text-green-800', icon: CheckCircle },
            cancelled: { text: 'Отменена', color: 'bg-red-100 text-red-800', icon: XCircle },
        };
        return map[status] || map.pending;
    };

    const statusInfo = getStatusInfo(transaction.status);
    const StatusIcon = statusInfo.icon;

    const handleConfirmPayment = async (file) => {
        try {
            const response = await p2pService.confirmPayment(transaction.id, file);
            if (response.success) {
                alert('Чек загружен!');
                setShowUploadModal(false);
                onRefresh();
            } else {
                alert(response.message || 'Ошибка');
            }
        } catch (error) {
            alert('Ошибка при загрузке чека');
        }
    };

    const handleComplete = async () => {
        try {
            console.log('🔄 Начинаем завершение сделки:', {
                transactionId: transaction.id,
                transaction_id_code: transaction.transaction_id,
                status: transaction.status,
                seller_id: transaction.seller_id,
                currentUserId
            });

            const response = await p2pService.completeTransaction(transaction.id);

            console.log('📥 Ответ от сервера при завершении:', response);

            if (response.success) {
                alert('Сделка завершена!');
                setShowConfirmComplete(false);
                onRefresh();
            } else {
                console.error('❌ Ошибка от сервера:', response);
                alert(response.message || 'Ошибка при завершении сделки');
                setShowConfirmComplete(false);
            }
        } catch (error) {
            console.error('❌ Exception при завершении сделки:', error);
            alert(`Ошибка: ${error.message || 'Неизвестная ошибка'}`);
            setShowConfirmComplete(false);
        }
    };

    const handleCancel = async (reason) => {
        try {
            const response = await p2pService.cancelTransaction(transaction.id, reason);
            if (response.success) {
                alert('Сделка отменена');
                setShowCancelModal(false);
                onRefresh();
            } else {
                alert(response.message || 'Ошибка');
            }
        } catch (error) {
            alert('Ошибка при отмене');
        }
    };

    const canUploadProof = isBuyer && transaction.status === 'pending' && !transaction.payment_proof;
    const canCancelAsBuyer = isBuyer && transaction.status === 'pending' && !transaction.payment_proof;
    const canConfirmComplete = isSeller && transaction.status === 'payment_sent';
    const canCancelAsSeller = false; // Продавец не может отменить после отправки чека
    const canViewProof = transaction.status === 'payment_sent' && transaction.payment_proof;

    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
            <div className="flex flex-col gap-4">
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1 sm:gap-2 ${statusInfo.color}`}>
                            <StatusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">{statusInfo.text}</span>
                            <span className="sm:hidden">
                                {transaction.status === 'pending' && 'Ожидание'}
                                {transaction.status === 'payment_sent' && 'Отправлено'}
                                {transaction.status === 'completed' && 'Завершено'}
                                {transaction.status === 'cancelled' && 'Отменено'}
                            </span>
                        </span>
                        {/* Индикатор роли пользователя */}
                        {isBuyer && (
                            <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bg-blue-100 text-blue-800">
                                <span className="hidden sm:inline">Вы покупатель</span>
                                <span className="sm:hidden">Покупатель</span>
                            </span>
                        )}
                        {isSeller && (
                            <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800">
                                <span className="hidden sm:inline">Вы продавец</span>
                                <span className="sm:hidden">Продавец</span>
                            </span>
                        )}
                    </div>

                    <p className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                        {Number(transaction.amount).toLocaleString()} {transaction.currency}
                    </p>

                    <div className="space-y-1 text-xs sm:text-sm text-gray-600">
                        <p className="break-all">ID: {transaction.transaction_id}</p>
                        <p>Покупатель: {transaction.buyer?.login || 'Неизвестно'} {isBuyer && '(Вы)'}</p>
                        <p>Продавец: {transaction.seller?.login || 'Неизвестно'} {isSeller && '(Вы)'}</p>

                        {/* Реквизиты из offer для покупателя */}
                        {isBuyer && transaction.offer?.payment_details && (
                            <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-1 text-xs sm:text-sm">💳 Реквизиты для оплаты:</p>
                                <p className="whitespace-pre-wrap text-gray-700 text-xs sm:text-sm break-all">{transaction.offer.payment_details}</p>
                            </div>
                        )}

                        {/* Условия из offer */}
                        {isBuyer && transaction.offer?.terms && (
                            <div className="mt-2 p-3 bg-gray-50 border-l-4 border-gray-400 rounded-lg">
                                <p className="font-semibold text-gray-700 mb-1 text-xs sm:text-sm">📋 Условия сделки:</p>
                                <p className="text-gray-600 text-xs sm:text-sm">{transaction.offer.terms}</p>
                            </div>
                        )}

                        {transaction.payment_deadline && transaction.status === 'pending' && (
                            <p className="text-red-600 font-semibold mt-2 text-xs sm:text-sm">
                                ⏰ Оплатить до: {new Date(transaction.payment_deadline).toLocaleString('ru-RU')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                    {canUploadProof && (
                        <button
                            onClick={() => setShowUploadModal(true)}
                            className="w-full bg-[#2b6c8e] text-white px-4 py-2 rounded-lg hover:bg-[#234d6a] flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            <Upload className="w-4 h-4" />
                            Загрузить чек
                        </button>
                    )}

                    {canCancelAsBuyer && (
                        <button
                            onClick={() => setShowCancelModal(true)}
                            className="w-full bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 text-sm sm:text-base"
                        >
                            Отменить
                        </button>
                    )}

                    {canViewProof && (
                        <button
                            onClick={() => setShowProofModal(true)}
                            className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            <Eye className="w-4 h-4" />
                            Посмотреть чек
                        </button>
                    )}

                    {canConfirmComplete && (
                        <button
                            onClick={() => setShowConfirmComplete(true)}
                            className="w-full bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm sm:text-base"
                        >
                            Подтвердить получение
                        </button>
                    )}

                    {canCancelAsSeller && (
                        <button
                            onClick={() => setShowCancelModal(true)}
                            className="w-full bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 text-sm sm:text-base"
                        >
                            Отменить сделку
                        </button>
                    )}
                </div>
            </div>

            {showUploadModal && <UploadProofModal onClose={() => setShowUploadModal(false)} onUpload={handleConfirmPayment} />}
            {showCancelModal && <CancelTransactionModal onClose={() => setShowCancelModal(false)} onCancel={handleCancel} />}
            {showConfirmComplete && (
                <ConfirmModal
                    isOpen={showConfirmComplete}
                    onClose={() => setShowConfirmComplete(false)}
                    onConfirm={handleComplete}
                    title="Подтверждение получения"
                    message="Вы подтверждаете получение оплаты?"
                    confirmText="Подтвердить"
                    cancelText="Отмена"
                    type="success"
                />
            )}
            {showProofModal && transaction.payment_proof && (
                <ProofViewModal proofUrl={transaction.payment_proof} onClose={() => setShowProofModal(false)} />
            )}
        </div>
    );
};

const CreateOfferModal = ({ onClose, onCreate }) => {
    const [formData, setFormData] = useState({
        type: 'sell',
        currency: 'USD',
        amount: '',
        min_amount: '',
        terms: '',
        payment_details: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onCreate({
            ...formData,
            amount: parseFloat(formData.amount),
            min_amount: formData.min_amount ? parseFloat(formData.min_amount) : null,
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Создать объявление</h2>
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Тип</label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="sell">Продажа</option>
                            <option value="buy">Покупка</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Валюта</label>
                        <select
                            value={formData.currency}
                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            <option value="USD">USDT</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Сумма</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            required
                            min="0.01"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Мин. сумма</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.min_amount}
                            onChange={(e) => setFormData({ ...formData, min_amount: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Реквизиты *</label>
                        <textarea
                            value={formData.payment_details}
                            onChange={(e) => setFormData({ ...formData, payment_details: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            rows="3"
                            required
                            placeholder="Номер карты или счета"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Условия</label>
                        <textarea
                            value={formData.terms}
                            onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            rows="2"
                        />
                    </div>

                    <div className="flex gap-3 pt-2 sm:pt-4">
                        <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg text-sm sm:text-base">
                            Отмена
                        </button>
                        <button type="submit" className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-[#2b6c8e] text-white rounded-lg text-sm sm:text-base">
                            Создать
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CreateTransactionModal = ({ offer, onClose, onCreate }) => {
    const [amount, setAmount] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onCreate({ offer_id: offer.id, amount: parseFloat(amount) });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Создать сделку</h2>

                <div className="bg-gray-50 rounded-lg p-4 mb-4 sm:mb-6">
                    <p className="text-lg sm:text-xl font-bold">{Number(offer.available_amount).toLocaleString()} {offer.currency}</p>
                    {offer.payment_details && (
                        <div className="mt-3 pt-3 border-t">
                            <p className="text-xs sm:text-sm font-semibold mb-1">Реквизиты:</p>
                            <p className="text-xs sm:text-sm break-all">{offer.payment_details}</p>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Сумма</label>
                        <input
                            type="number"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                            required
                            min={offer.min_amount || 0.01}
                            max={offer.available_amount}
                        />
                    </div>

                    <div className="flex gap-3 pt-2 sm:pt-4">
                        <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-lg text-sm sm:text-base">
                            Отмена
                        </button>
                        <button type="submit" className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-[#2b6c8e] text-white rounded-lg text-sm sm:text-base">
                            Создать
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const UploadProofModal = ({ onClose, onUpload }) => {
    const [file, setFile] = useState(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (file) onUpload(file);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Загрузить чек</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="w-full px-4 py-2 border rounded-lg text-sm"
                        required
                    />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border rounded-lg text-sm sm:text-base">
                            Отмена
                        </button>
                        <button type="submit" disabled={!file} className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-[#2b6c8e] text-white rounded-lg disabled:bg-gray-300 text-sm sm:text-base">
                            Загрузить
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const CancelTransactionModal = ({ onClose, onCancel }) => {
    const [reason, setReason] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onCancel(reason);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Отменить сделку</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg text-sm sm:text-base"
                        rows="4"
                        required
                        placeholder="Причина отмены"
                    />
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border rounded-lg text-sm sm:text-base">
                            Назад
                        </button>
                        <button type="submit" className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-red-500 text-white rounded-lg text-sm sm:text-base">
                            Отменить
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ProofViewModal = ({ proofUrl, onClose }) => {
    // Получаем полный URL изображения
    const fullProofUrl = getImageUrl(proofUrl);
    const isImage = proofUrl.match(/\.(jpg|jpeg|png)$/i);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold">Чек об оплате</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
                </div>

                <div className="flex justify-center">
                    {isImage ? (
                        <img src={fullProofUrl} alt="Payment proof" className="max-w-full max-h-[70vh] rounded-lg" />
                    ) : (
                        <a href={fullProofUrl} target="_blank" rel="noopener noreferrer" className="bg-[#2b6c8e] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base">
                            Открыть файл
                        </a>
                    )}
                </div>

                <button onClick={onClose} className="mt-4 w-full px-4 sm:px-6 py-2 sm:py-3 border rounded-lg text-sm sm:text-base">
                    Закрыть
                </button>
            </div>
        </div>
    );
};

export default P2PExchange;
